import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import api from '../services/api';
import './CloudBlue.css';

interface CloudBlueSubscription {
  id: string;
  name?: string;
  status: string;
  product?: {
    id: string;
    name: string;
  };
  tiers?: {
    customer?: {
      name: string;
      external_id: string;
    };
  };
  created: string;
  period?: string;
  billing_period?: string | null;
  subscription_period?: string | null;
  renewal_date?: string | null;
  expiration_date?: string | null;
  billing_model?: string | null;
  quantity?: number;
  total_price?: string;
  currency?: string;
  products_detail?: Array<{
    name: string;
    quantity: number;
    unitPrice?: { currency: string; amount: string };
  }>;
}

interface CloudBlueCustomer {
  id: string;
  name: string;
  external_id?: string;
  active_subscriptions?: number;
  active_quantity?: number;
  has_quantities?: boolean;   // false = no seats on any active subscription -> red, nothing goes to Invoice Prep
}

interface ServiceMapping {
  id: number;
  customer_id: number;
  customer_name: string;
  external_id: string;
  external_name: string;
  service_data: any;
  mapped_at: string;
  products: any[];
}

function CloudBlue() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'customers' | 'sync-log'>('customers');
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [runChanges, setRunChanges] = useState<Record<string, any[]>>({});
  const [customers, setCustomers] = useState<CloudBlueCustomer[]>([]);
  const [mappings, setMappings] = useState<ServiceMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localCustomers, setLocalCustomers] = useState<any[]>([]);
  const [expandedCustomer, setExpandedCustomer] = useState<string | null>(null);
  const [customerSubs, setCustomerSubs] = useState<Record<string, CloudBlueSubscription[]>>({});
  const [loadingSubs, setLoadingSubs] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'matched' | 'unmatched' | 'hidden'>('all');
  // Manual hide (remembered in this browser), same pattern as the Splynx / PortaOne pages
  const HIDDEN_KEY = 'cv-cloudblue-hidden-customers';
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try { return new Set<string>(JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]')); } catch { return new Set<string>(); }
  });
  const saveHidden = (n: Set<string>) => { try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(n))); } catch { /* ignore */ } };
  const toggleHidden = (id: string) => setHidden(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); saveHidden(n); return n; });
  const unhideAll = () => { const n = new Set<string>(); saveHidden(n); setHidden(n); if (filter === 'hidden') setFilter('all'); };
  const [mappingCustomerId, setMappingCustomerId] = useState<string | null>(null);
  const [mappingCustomerName, setMappingCustomerName] = useState<string>('');
  const [selectedLocalCustomer, setSelectedLocalCustomer] = useState<number | null>(null);
  const [modalSearch, setModalSearch] = useState('');


  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin' || user.is_superuser);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  const loadCustomers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await api.get('/cloudblue/customers');
      setCustomers(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load CloudBlue customers:', err);
      setError('Failed to load customers');
      toast.error('Failed to load CloudBlue customers');
    }
  }, [isAdmin]);

  const loadMappings = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await api.get('/cloudblue/mappings');
      setMappings(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load CloudBlue mappings:', err);
      setError('Failed to load mappings');
      toast.error('Failed to load CloudBlue mappings');
    }
  }, [isAdmin]);

  const loadSyncStatus = useCallback(async () => {
    if (!isAdmin) return;
    setSyncStatusLoading(true);
    try {
      const response = await api.get('/cloudblue/sync-status', { params: { runs: 30 } });
      setSyncStatus(response.data);
    } catch (err: any) {
      console.error('Failed to load sync status:', err);
      toast.error('Failed to load sync status');
    } finally {
      setSyncStatusLoading(false);
    }
  }, [isAdmin]);

  const loadRunChanges = useCallback(async (runId: string) => {
    if (runChanges[runId]) return;
    try {
      const response = await api.get('/cloudblue/changes', { params: { limit: 500 } });
      const all = response.data.data || [];
      const forRun = all.filter((c: any) => c.sync_run_id === runId);
      setRunChanges(prev => ({ ...prev, [runId]: forRun }));
    } catch (err: any) {
      console.error('Failed to load run changes:', err);
    }
  }, [runChanges]);

  const toggleRun = useCallback((runId: string) => {
    if (expandedRun === runId) {
      setExpandedRun(null);
    } else {
      setExpandedRun(runId);
      loadRunChanges(runId);
    }
  }, [expandedRun, loadRunChanges]);

  const loadLocalCustomers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await api.get('/customers');
      setLocalCustomers(Array.isArray(response.data) ? response.data : response.data.customers || []);
    } catch (err: any) {
      console.error('Failed to load local customers:', err);
      toast.error('Failed to load local customers');
    }
  }, [isAdmin]);

  const loadCustomerSubscriptions = useCallback(async (customerId: string) => {
    if (customerSubs[customerId]) return; // already loaded
    setLoadingSubs(customerId);
    try {
      const response = await api.get('/cloudblue/subscriptions', {
        params: { customer_id: customerId, limit: 500 }
      });
      setCustomerSubs(prev => ({
        ...prev,
        [customerId]: response.data.data || []
      }));
    } catch (err: any) {
      console.error('Failed to load subscriptions for customer:', err);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoadingSubs(null);
    }
  }, [customerSubs]);

  const toggleCustomer = useCallback((customerId: string) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
    } else {
      setExpandedCustomer(customerId);
      loadCustomerSubscriptions(customerId);
    }
  }, [expandedCustomer, loadCustomerSubscriptions]);

  const handleSync = useCallback(async () => {
    if (!isAdmin) return;
    setSyncing(true);
    try {
      await api.post('/cloudblue/sync');
      toast.success('CloudBlue data sync completed successfully');
      setCustomerSubs({}); // clear cached data
      await Promise.all([loadCustomers(), loadMappings()]);
    } catch (err: any) {
      console.error('Sync failed:', err);
      toast.error('Failed to sync CloudBlue data');
    } finally {
      setSyncing(false);
    }
  }, [isAdmin, loadCustomers, loadMappings]);

  const createMapping = useCallback(async (
    customerId: number,
    externalId: string,
    externalName: string,
    serviceData: any
  ) => {
    try {
      const response = await api.post('/cloudblue/mappings', {
        customer_id: customerId,
        external_id: externalId,
        external_name: externalName,
        service_data: serviceData || {}
      });

      if (response.data.success) {
        toast.success('CloudBlue mapping created successfully');
        await loadMappings();
      }
    } catch (err: any) {
      console.error('Failed to create mapping:', err);
      toast.error(err.response?.data?.detail || 'Failed to create CloudBlue mapping');
    }
  }, [loadMappings]);

  const handleMapCustomer = useCallback(async () => {
    if (!mappingCustomerId || !selectedLocalCustomer) {
      toast.error('Select a customer');
      return;
    }
    await createMapping(
      selectedLocalCustomer,
      mappingCustomerId,
      mappingCustomerName,
      { customer_id: mappingCustomerId }
    );
    setMappingCustomerId(null);
    setMappingCustomerName('');
    setSelectedLocalCustomer(null);
    setModalSearch('');
  }, [mappingCustomerId, mappingCustomerName, selectedLocalCustomer, createMapping]);

  const deleteMapping = useCallback(async (mappingId: number) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;

    try {
      const response = await api.delete(`/cloudblue/mappings/${mappingId}`);
      if (response.data.success) {
        toast.success('CloudBlue mapping deleted successfully');
        await loadMappings();
      }
    } catch (err: any) {
      console.error('Failed to delete mapping:', err);
      toast.error('Failed to delete CloudBlue mapping');
    }
  }, [loadMappings]);

  const loadData = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      await Promise.all([loadCustomers(), loadMappings(), loadLocalCustomers()]);
    } catch (err) {
      console.error('Failed to load CloudBlue data:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, loadCustomers, loadMappings, loadLocalCustomers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'sync-log') {
      loadSyncStatus();
    }
  }, [activeTab, loadSyncStatus]);

  if (authLoading || loading) {
    return <div className="cloudblue-loading">Loading CloudBlue Connect...</div>;
  }

  if (!isAdmin) {
    return null;
  }

  // Build set of mapped CloudBlue customer IDs
  const mappedExternalIds = new Set(mappings.map(m => m.external_id));
  const shownCustomers = customers.filter(c => !hidden.has(c.id));   // counts exclude hidden rows
  const hiddenCount = customers.length - shownCustomers.length;
  const matchedCount = shownCustomers.filter(c => mappedExternalIds.has(c.id)).length;
  const unmatchedCount = shownCustomers.length - matchedCount;

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(customerSearch.toLowerCase());
    const isHidden = hidden.has(c.id);
    if (filter === 'hidden') return matchesSearch && isHidden;
    if (isHidden) return false;
    const isMapped = mappedExternalIds.has(c.id);
    const matchesFilter = filter === 'all' || (filter === 'matched' && isMapped) || (filter === 'unmatched' && !isMapped);
    return matchesSearch && matchesFilter;
  });

  const renderCustomers = () => (
    <div className="cloudblue-section">
      <div className="cloudblue-table-container">
        <table className="cloudblue-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>Name</th>
              <th>ID</th>
              <th>Matched Customer</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map(customer => (
              <React.Fragment key={customer.id}>
                <tr
                  className={`customer-row ${expandedCustomer === customer.id ? 'expanded' : ''} ${customer.has_quantities === false ? 'no-quantities' : ''}`}
                  onClick={() => toggleCustomer(customer.id)}
                  style={{ cursor: 'pointer' }}
                  title={customer.has_quantities === false ? 'No quantities on any active subscription - nothing is billed for this customer and no lines appear in Invoice Prep' : undefined}
                >
                  <td className="expand-icon">
                    {expandedCustomer === customer.id ? '▼' : '▶'}
                  </td>
                  <td>
                    <strong>{customer.name}</strong>
                    {customer.has_quantities === false && <span className="match-badge danger cb-noqty" title="No active quantities">0 qty · not billed</span>}
                    {customer.has_quantities && <span className="cb-qty" title="Seats across active subscriptions">{customer.active_quantity} qty</span>}
                  </td>
                  <td>{customer.external_id || customer.id}</td>
                  <td>
                    {(() => {
                      const mapping = mappings.find(m => m.external_id === customer.id);
                      return mapping ? (
                        <span className="match-badge success">{mapping.customer_name}</span>
                      ) : (
                        <span className="match-badge warning">No match</span>
                      );
                    })()}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const mapping = mappings.find(m => m.external_id === customer.id);
                      return mapping ? (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => {
                            setMappingCustomerId(customer.id);
                            setMappingCustomerName(customer.name);
                            setSelectedLocalCustomer(mapping.customer_id);
                            setModalSearch('');
                          }}
                        >
                          Change
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => {
                            setMappingCustomerId(customer.id);
                            setMappingCustomerName(customer.name);
                            setSelectedLocalCustomer(null);
                            setModalSearch('');
                          }}
                        >
                          Map
                        </button>
                      );
                    })()}
                    <button
                      className="btn btn-sm btn-outline cb-hide-btn"
                      onClick={() => toggleHidden(customer.id)}
                      title={hidden.has(customer.id) ? 'Unhide this customer' : 'Hide this customer from the list and counts (remembered in this browser)'}
                    >
                      {hidden.has(customer.id) ? '👁 Unhide' : '🙈 Hide'}
                    </button>
                  </td>
                </tr>
                {expandedCustomer === customer.id && (
                  <tr className="subscription-detail-row">
                    <td colSpan={5}>
                      {loadingSubs === customer.id ? (
                        <div className="subs-loading">Loading subscriptions...</div>
                      ) : (
                        <div className="customer-subscriptions">
                          {(customerSubs[customer.id] || []).length === 0 ? (
                            <div className="subs-empty">No subscriptions found</div>
                          ) : (
                            <table className="subs-table">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>Qty</th>
                                  <th>Price</th>
                                  <th title="How often CloudBlue bills this subscription">Billing period</th>
                                  <th title="Contract term of the subscription">Subscription period</th>
                                  <th>Renews</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(customerSubs[customer.id] || []).map(sub => (
                                  <tr key={sub.id}>
                                    <td>{sub.product?.name || sub.name || 'N/A'}</td>
                                    <td>{sub.quantity ?? '—'}</td>
                                    <td>{sub.total_price ? `R${sub.total_price}` : '—'}</td>
                                    <td>{sub.billing_period || sub.period || '—'}{sub.billing_model === 'chargeBeforeBillingPeriod' ? <span className="cb-muted" title="Charged in advance"> · in advance</span> : ''}</td>
                                    <td>{sub.subscription_period || '—'}</td>
                                    <td title={sub.expiration_date ? `Expires ${sub.expiration_date}` : ''}>{sub.renewal_date || '—'}</td>
                                  </tr>
                                ))}
                                {(() => {
                                  const subs = customerSubs[customer.id] || [];
                                  const total = subs.reduce((sum, s) => sum + (parseFloat(s.total_price || '0')), 0);
                                  const totalQty = subs.reduce((sum, s) => sum + (s.quantity || 0), 0);
                                  return total > 0 ? (
                                    <tr className="subs-total">
                                      <td><strong>Total</strong></td>
                                      <td><strong>{totalQty}</strong></td>
                                      <td><strong>R{total.toFixed(2)}</strong></td>
                                      <td colSpan={3}></td>
                                    </tr>
                                  ) : null;
                                })()}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filteredCustomers.length === 0 && (
          <div className="cloudblue-empty">No customers found</div>
        )}
      </div>
    </div>
  );

  const freshnessLabel = (f?: string) => {
    if (f === 'fresh') return { text: 'Fresh', className: 'freshness-fresh' };
    if (f === 'stale') return { text: 'Stale', className: 'freshness-stale' };
    if (f === 'very_stale') return { text: 'Very stale', className: 'freshness-very-stale' };
    return { text: 'Unknown', className: 'freshness-unknown' };
  };

  const formatHours = (h?: number | null) => {
    if (h === null || h === undefined) return '—';
    if (h < 1) return `${Math.round(h * 60)} min ago`;
    if (h < 48) return `${h.toFixed(1)} h ago`;
    return `${(h / 24).toFixed(1)} d ago`;
  };

  const renderSyncLog = () => {
    if (syncStatusLoading) {
      return <div className="cloudblue-loading">Loading sync status…</div>;
    }
    if (!syncStatus) {
      return (
        <div className="cloudblue-section">
          <button className="btn btn-outline" onClick={loadSyncStatus}>Load sync status</button>
        </div>
      );
    }
    const f = freshnessLabel(syncStatus.freshness);
    return (
      <div className="cloudblue-section">
        <div className="sync-status-header">
          <div className={`sync-freshness-badge ${f.className}`}>{f.text}</div>
          <div className="sync-status-meta">
            <div><strong>Last sync:</strong> {syncStatus.last_synced_at ? new Date(syncStatus.last_synced_at).toLocaleString() : 'never'}</div>
            <div><strong>{formatHours(syncStatus.hours_since_sync)}</strong></div>
          </div>
          <div className="sync-status-counts">
            <span><strong>{syncStatus.counts?.customers ?? 0}</strong> customers</span>
            <span><strong>{syncStatus.counts?.subscriptions ?? 0}</strong> subs</span>
            <span><strong>{syncStatus.counts?.changes_total ?? 0}</strong> changes logged</span>
          </div>
          <button className="btn btn-outline" onClick={loadSyncStatus}>Refresh</button>
        </div>

        <h3 className="sync-section-title">Recent sync runs</h3>
        <div className="cloudblue-table-container">
          <table className="cloudblue-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>When</th>
                <th>Run ID</th>
                <th>Changes</th>
                <th>Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {(syncStatus.recent_runs || []).map((run: any) => (
                <React.Fragment key={run.sync_run_id}>
                  <tr
                    className={`customer-row ${expandedRun === run.sync_run_id ? 'expanded' : ''}`}
                    onClick={() => toggleRun(run.sync_run_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="expand-icon">{expandedRun === run.sync_run_id ? '▼' : '▶'}</td>
                    <td>{run.started_at ? new Date(run.started_at).toLocaleString() : '—'}</td>
                    <td><code>{run.sync_run_id}</code></td>
                    <td><strong>{run.change_count}</strong></td>
                    <td>
                      {Object.entries(run.by_type || {}).map(([k, v]: any) => (
                        <span key={k} className={`change-chip change-${k}`}>
                          {k}: {v}
                        </span>
                      ))}
                    </td>
                  </tr>
                  {expandedRun === run.sync_run_id && (
                    <tr className="subscription-detail-row">
                      <td colSpan={5}>
                        <div className="customer-subscriptions">
                          {(runChanges[run.sync_run_id] || []).length === 0 ? (
                            <div className="subs-loading">Loading changes…</div>
                          ) : (
                            <table className="subs-table">
                              <thead>
                                <tr>
                                  <th>Entity</th>
                                  <th>Change</th>
                                  <th>Name / ID</th>
                                  <th>Detail</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(runChanges[run.sync_run_id] || []).map((c: any) => (
                                  <tr key={c.id}>
                                    <td>{c.entity_type}</td>
                                    <td><span className={`change-chip change-${c.change_type}`}>{c.change_type}</span></td>
                                    <td>
                                      <strong>{c.entity_name || c.entity_external_id}</strong>
                                      {c.entity_name && (
                                        <span className="muted"> ({c.entity_external_id})</span>
                                      )}
                                    </td>
                                    <td>
                                      {c.field_changed ? (
                                        <span>
                                          {c.field_changed}: <code>{c.old_value || '∅'}</code> → <code>{c.new_value || '∅'}</code>
                                        </span>
                                      ) : (
                                        <span className="muted">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {(syncStatus.recent_runs || []).length === 0 && (
                <tr><td colSpan={5} className="cloudblue-empty">No sync runs recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };


  return (
    <div className="cloudblue-container">
      <div className="cloudblue-header">
        <h1>CloudBlue Connect Integration</h1>
        <div className="cloudblue-actions">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="sync-btn"
          >
            {syncing ? 'Syncing...' : 'Sync Data'}
          </button>
        </div>
      </div>

      {error && (
        <div className="cloudblue-error">
          Error: {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-row">
        <div className="summary-card">
          <div className="summary-value">{shownCustomers.length}</div>
          <div className="summary-label">Total Customers{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}</div>
        </div>
        <div className="summary-card success">
          <div className="summary-value">{matchedCount}</div>
          <div className="summary-label">Matched</div>
        </div>
        <div className="summary-card warning">
          <div className="summary-value">{unmatchedCount}</div>
          <div className="summary-label">Unmatched</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="cloudblue-tabs">
        <button
          className={`tab-btn ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          Customers
        </button>
        <button
          className={`tab-btn ${activeTab === 'sync-log' ? 'active' : ''}`}
          onClick={() => setActiveTab('sync-log')}
        >
          Sync Log
        </button>
      </div>

      {activeTab === 'customers' && (<>
      {/* Filters */}
      <div className="filters-row">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({shownCustomers.length})
          </button>
          <button
            className={`filter-btn ${filter === 'matched' ? 'active' : ''}`}
            onClick={() => setFilter('matched')}
          >
            Matched ({matchedCount})
          </button>
          <button
            className={`filter-btn ${filter === 'unmatched' ? 'active' : ''}`}
            onClick={() => setFilter('unmatched')}
          >
            Unmatched ({unmatchedCount})
          </button>
          {hiddenCount > 0 && (
            <>
              <button
                className={`filter-btn ${filter === 'hidden' ? 'active' : ''}`}
                onClick={() => setFilter(filter === 'hidden' ? 'all' : 'hidden')}
                title="Show only the customers you have hidden"
              >
                🙈 Hidden ({hiddenCount})
              </button>
              {filter === 'hidden' && (
                <button className="filter-btn" onClick={unhideAll}>Unhide all</button>
              )}
            </>
          )}
        </div>
        <input
          type="text"
          className="cloudblue-search"
          placeholder="Search customers..."
          value={customerSearch}
          onChange={(e) => setCustomerSearch(e.target.value)}
        />
      </div>

      <div className="cloudblue-content">
        {renderCustomers()}
      </div>
      </>)}

      {activeTab === 'sync-log' && (
        <div className="cloudblue-content">
          {renderSyncLog()}
        </div>
      )}

      {/* Mapping Modal */}
      {mappingCustomerId && (
        <div className="modal-overlay" onClick={() => { setMappingCustomerId(null); setModalSearch(''); }}>
          <div className="modal modal-tall" onClick={e => e.stopPropagation()}>
            <h2>Map to CloudView Customer</h2>
            <p className="modal-info">
              CloudBlue: <strong>{mappingCustomerName}</strong>
            </p>
            <div className="form-group">
              <label>Search Customer</label>
              <input
                type="text"
                className="input"
                placeholder="Type to search customers..."
                value={modalSearch}
                onChange={e => setModalSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Select Customer ({localCustomers.filter(c =>
                !modalSearch || (c.xero_name || c.name || '').toLowerCase().includes(modalSearch.toLowerCase())
              ).length} matches)</label>
              <div className="customer-list">
                {localCustomers
                  .filter(c => !modalSearch || (c.xero_name || c.name || '').toLowerCase().includes(modalSearch.toLowerCase()))
                  .slice(0, 100)
                  .map(c => (
                    <div
                      key={c.id}
                      className={`customer-option ${selectedLocalCustomer === c.id ? 'selected' : ''}`}
                      onClick={() => setSelectedLocalCustomer(c.id)}
                    >
                      {c.xero_name || c.name}
                    </div>
                  ))}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleMapCustomer} disabled={!selectedLocalCustomer}>
                {mappings.find(m => m.external_id === mappingCustomerId) ? 'Update' : 'Map'}
              </button>
              <button className="btn btn-outline" onClick={() => { setMappingCustomerId(null); setModalSearch(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CloudBlue;
